import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, Eye, LogOut, ArrowLeft, ChevronDown, ScrollText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

/**
 * Persistent admin toolbar shown whenever the *real* session is admin.
 *
 * Unified View-As model: selecting "View as User" or "View as Dealer" starts a
 * full impersonation session against a dedicated sandbox account. Everything —
 * UI route guards AND backend authorization — honors the impersonated identity,
 * so the admin sees the exact experience that role gets. Selecting
 * "Admin (own account)" stops impersonation.
 */
export default function AdminToolbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    impersonation, isImpersonating, stopImpersonation, startViewAsRole,
    realUser, isAdminReal,
  } = useAuth();
  const [busy, setBusy] = useState(false);

  // Track previous path so "Exit" returns there.
  useEffect(() => {
    if (!isImpersonating) return;
    if (!sessionStorage.getItem("financelocal:imp_return_to")) {
      sessionStorage.setItem("financelocal:imp_return_to", location.pathname);
    }
  }, [isImpersonating, location.pathname]);

  if (!isAdminReal) return null;

  const currentRoleLabel = isImpersonating ? impersonation?.target?.role : "admin";

  const handleViewAs = async (role) => {
    setBusy(true);
    try {
      if (role === "admin") {
        if (isImpersonating) await stopImpersonation();
        sessionStorage.removeItem("financelocal:imp_return_to");
        navigate("/admin");
        toast.success("Returned to your admin account");
        return;
      }
      // user or dealer
      sessionStorage.setItem("financelocal:imp_return_to", location.pathname);
      await startViewAsRole(role);
      toast.success(`Viewing as ${role}`);
      navigate(role === "dealer" ? "/dealer" : "/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to switch to ${role}`);
    } finally { setBusy(false); }
  };

  const handleExit = async () => {
    setBusy(true);
    try {
      await stopImpersonation();
      const ret = sessionStorage.getItem("financelocal:imp_return_to");
      sessionStorage.removeItem("financelocal:imp_return_to");
      navigate(ret && ret !== location.pathname ? ret : "/admin");
    } finally { setBusy(false); }
  };

  const handleReturnAdminDashboard = async () => {
    setBusy(true);
    try {
      if (isImpersonating) await stopImpersonation();
      sessionStorage.removeItem("financelocal:imp_return_to");
      navigate("/admin");
    } finally { setBusy(false); }
  };

  const dashHref = () => {
    if (isImpersonating) {
      return impersonation?.target?.role === "dealer" ? "/dealer" : "/dashboard";
    }
    return "/admin";
  };

  const banner = isImpersonating ? (
    <div data-testid="impersonation-banner"
      className="bg-amber-100 dark:bg-amber-900/40 border-b border-amber-400 dark:border-amber-700 px-4 py-2 text-sm text-amber-950 dark:text-amber-100 flex flex-wrap items-center gap-3">
      <Shield className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-[200px]">
        <b>You are currently viewing this account as Administrator.</b>{" "}
        <span className="opacity-90">
          ({impersonation.target?.email} · role: <b>{impersonation.target?.role}</b>) ·
          All actions are being audit logged.
        </span>
      </div>
      <Button size="sm" variant="outline" className="rounded-none uppercase text-xs h-8 bg-white/70 dark:bg-black/30"
        onClick={handleExit} disabled={busy} data-testid="imp-exit-btn">
        <LogOut className="w-3 h-3 mr-1" /> Exit Impersonation
      </Button>
      <Button size="sm" className="rounded-none uppercase text-xs h-8"
        onClick={handleReturnAdminDashboard} disabled={busy}
        data-testid="imp-return-admin-btn">
        <ArrowLeft className="w-3 h-3 mr-1" /> Return to Admin Dashboard
      </Button>
    </div>
  ) : null;

  return (
    <>
      <div data-testid="admin-toolbar"
        className="bg-zinc-900 text-zinc-100 dark:bg-zinc-800 border-b border-zinc-700 text-xs">
        <div className="mx-auto max-w-7xl px-4 h-9 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1.5 font-mono-data uppercase tracking-wider text-amber-300">
            <Shield className="w-3.5 h-3.5" /> Admin Mode
          </span>
          <span className="opacity-70 hidden sm:inline">·</span>
          <span className="opacity-80 hidden sm:inline truncate max-w-[200px]">{realUser?.email}</span>

          <span className="ml-auto flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="view-as-trigger" disabled={busy}
                  className="flex items-center gap-1 px-2 h-7 bg-zinc-800 dark:bg-zinc-700 hover:bg-zinc-700 border border-zinc-700 disabled:opacity-50">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                  View As: <b className="ml-1">{currentRoleLabel}</b>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-64">
                <DropdownMenuLabel className="font-overline">View as Role</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="view-as-admin" onClick={() => handleViewAs("admin")}>
                  Admin (own account)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="view-as-user" onClick={() => handleViewAs("user")}>
                  User (sandbox account)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="view-as-dealer" onClick={() => handleViewAs("dealer")}>
                  Dealer (sandbox account)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="goto-impersonation-logs"
                  onClick={() => navigate("/admin/impersonation-logs")}>
                  <ScrollText className="w-3.5 h-3.5 mr-2" /> Impersonation Audit Log
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button data-testid="admin-toolbar-goto-dash"
              onClick={() => navigate(dashHref())}
              className="px-2 h-7 bg-zinc-800 dark:bg-zinc-700 hover:bg-zinc-700 border border-zinc-700">
              Open {currentRoleLabel} Dashboard
            </button>

            {isImpersonating && (
              <button data-testid="admin-toolbar-back-to-admin"
                onClick={handleReturnAdminDashboard} disabled={busy}
                className="px-2 h-7 bg-amber-500 text-zinc-900 hover:bg-amber-400 font-semibold disabled:opacity-50">
                ← Back to Admin
              </button>
            )}
          </span>
        </div>
      </div>
      {banner}
    </>
  );
}
